import { Socket } from "socket.io-client";
import { gameSituationBackend, sourcePlayer } from "../../implementationGlobals";
import {
  ComponentMoveData,
  SetVariableData,
  SetCounterData,
  MoveAllData,
  RevealAllData,
  CreateComponentData,
  RevealComponentData,
  MoveToBankData,
  DiscardComponentData,
  RevealToDeckData,
  SetGameStateData,
  AddSubstateData,
  SetGlobalData,
  SetGameStateVariable,
  ComponentOverdriveData,
  SetPlayerAttrData,
  GameMoveForEval,
  IncStatData,
  GameComponentForGame,
  GameMoveData,
} from "src/components/implementationTypes";
import { ComponentData, EventManager, GameSituationBackend, PlayerEvent, StatValue } from "src/components/types";
import { Ref, RefObject } from "react";

function getComponentDisplayMode(_gameSituation: GameSituationBackend, locationId?: string, positionName?: string) {
  let locationName = _gameSituation.getGameComponentById(locationId)?.name;
  let locationComponent = window.componentPool[locationName];
  let positionDisplayMode;
  if (positionName && locationComponent?.positions) {
    positionDisplayMode = locationComponent.positions[positionName]?.displayMode;
  }

  return positionDisplayMode ?? locationComponent?.displayMode;
}

export function createEventManager(
  socket: Socket,
  tableId: string,
  editMode: boolean,
  cheatMode: boolean,
  setCheatMode: Function,
  setConfirmMovesToSend: Function,
  allEventsRef: React.MutableRefObject<PlayerEvent[]>,
  tableStateRef: React.MutableRefObject<GameSituationBackend | undefined>,
  evalGameMove: Function,
  getImage: Function,
  createConfirm: Function,
  setStatusBarStatus: Function,
  turnToConfirm: RefObject<number>,
  newEventCallBack: Function,
  setAlert: Function,
  evalNextTableEvent: Function,
) {
  return {
    revealTempId: 0,
    _preventUndo: false as boolean | string,
    undoBreakpoint: false,
    confirmMessages: [] as string[],
    replayMarks: [] as string[],
    socket,
    tableId,
    inactiveCanPlay: false,
    newActivePlayer: "nobody" as string | undefined,
    oldActivePlayer: "nobody" as string | undefined,
    turnHidden: "",
    turnNonBlocking: false,
    movePreventReasons: [] as { [key: string]: any }[],
    addComponentMoveMove: function (options: ComponentMoveData, gameSituation?: GameSituationBackend) {
      return this.addGameMove(Object.assign(options, { type: "component move" }), gameSituation);
    },
    addSetOptionMove: function (options: SetVariableData, gameSituation?: GameSituationBackend) {
      return this.addGameMove(Object.assign(options, { type: "set component option" }), gameSituation);
    },
    addSetCounterMove: function (options: SetCounterData, gameSituation?: GameSituationBackend) {
      return this.addGameMove(Object.assign(options, { type: "set counter" }), gameSituation);
    },
    addMoveAllMove: function (options: MoveAllData, gameSituation?: GameSituationBackend) {
      return this.addGameMove(Object.assign(options, { type: "move all" }), gameSituation);
    },
    addRevealAllMove: function (options: RevealAllData, gameSituation?: GameSituationBackend) {
      return this.addGameMove(Object.assign(options, { type: "reveal all" }), gameSituation);
    },
    addComponentCreateMove: function (options: CreateComponentData, gameSituation?: GameSituationBackend) {
      return this.addGameMove(Object.assign(options, { type: "component create" }), gameSituation);
    },
    addComponentRevealMove: function (options: RevealComponentData, gameSituation?: GameSituationBackend) {
      return this.addGameMove(Object.assign(options, { type: "component reveal" }), gameSituation);
    },
    addRevealToDeckMove: function (options: RevealToDeckData, gameSituation?: GameSituationBackend) {
      return this.addGameMove(Object.assign(options, { type: "reveal to deck" }), gameSituation);
    },
    addMoveToBankMove: function (options: MoveToBankData, gameSituation?: GameSituationBackend) {
      return this.addGameMove(Object.assign(options, { type: "move to bank" }), gameSituation);
    },
    addDiscardComponentMove: function (options: DiscardComponentData, gameSituation?: GameSituationBackend) {
      return this.addGameMove(Object.assign(options, { type: "discard" }), gameSituation);
    },
    addSetGameStateMove: function (options: SetGameStateData, gameSituation?: GameSituationBackend) {
      return this.addGameMove(Object.assign(options, { type: "set gamestate" }), gameSituation);
    },
    addAddSubstateMove: function (options: AddSubstateData, gameSituation?: GameSituationBackend) {
      return this.addGameMove(Object.assign(options, { type: "add substate" }), gameSituation);
    },
    addSetGlobalMove: function (options: SetGlobalData, gameSituation?: GameSituationBackend) {
      return this.addGameMove(Object.assign(options, { type: "set global" }), gameSituation);
    },
    addSetGamestateVariableMove: function (options: SetGameStateVariable, gameSituation?: GameSituationBackend) {
      return this.addGameMove(Object.assign(options, { type: "set gamestate variable" }), gameSituation);
    },
    addComponentOverdriveMove: function (options: ComponentOverdriveData, gameSituation?: GameSituationBackend) {
      return this.addGameMove(Object.assign(options, { type: "set component overdrive" }), gameSituation);
    },
    addSetPlayerAttrMove: function (options: SetPlayerAttrData, gameSituation?: GameSituationBackend) {
      return this.addGameMove(Object.assign(options, { type: "set player attr" }), gameSituation);
    },
    addGameMove: function (options: GameMoveForEval, _gameSituation?: GameSituationBackend) {
      if (!_gameSituation) {
        _gameSituation = gameSituationBackend;
      }
      options.editMode = editMode;
      let tempId = undefined;
      if (options.type === "component reveal" || options.type === "reveal to deck") {
        if (!options.revealedComponent) {
          options.revealedComponent = { id: "dummy", children: {}, name: "unknown", options: {} };
        }
        tempId = options.revealedComponent.id = "__revealTempId" + this.revealTempId++;

        const { destinationId, positionName } = options as RevealComponentData;
        if (getComponentDisplayMode(_gameSituation, destinationId, positionName) === "opponentCardBack") {
          options.displayMode = "opponentCardBack";
        }
      }
      if (["discard", "return to top", "put to bottom"].includes(options.type ?? "")) {
        if (!options.componentData && options.componentId) {
          options.componentData = _gameSituation.getGameComponentById(options.componentId);
        }
        if (options.componentData?.childData) {
          delete options.componentData.childData;
        }
      }

      if (options.type === "game end") {
        options.stats = _gameSituation.stats as { [key: string]: StatValue };
        let allEvents = allEventsRef.current;
        options.stats.__gameTime = Math.floor(
          ((allEvents[allEvents.length - 1]?.timestamp ?? 0) - (allEvents[0]?.timestamp ?? 0)) / 1000 / 60,
        );
        if (_gameSituation.ended) {
          throw "The game has already ended (game end was probably called twice)";
        }
        _gameSituation.ended = true;
        options.playerStats = {};

        for (let p of _gameSituation.players) {
          for (let statName in p.stats) {
            if (!options.playerStats[statName]) {
              options.playerStats[statName] = {};
            }
            options.playerStats[statName][p.color] = p.stats[statName];
          }
        }
        options.objectStats = _gameSituation?.objectStats;
        //setAllStats({playerStats: options.playerStats, tableStats: options.stats});
      }

      if (options.type === "component move") {
        const componentData = _gameSituation.getGameComponentById(options.componentId);
        const displayMode = getComponentDisplayMode(
          _gameSituation,
          componentData?.parent,
          componentData?.childData?.positionName,
        );
        const cardId = componentData?.options?.cardId;
        if (displayMode === "opponentCardBack" && cardId && options.componentId) {
          // reveal cardId to all players (it was hidden to some players because of opponentCardBack display mode)
          this.addSetOptionMove(
            {
              componentId: options.componentId,
              optionName: "cardId",
              newValue: cardId,
            },
            _gameSituation,
          );
        }
      }

      this.gameMoveList.push(JSON.parse(JSON.stringify(options)));
      evalGameMove(options, _gameSituation);
      this.newActivePlayer = _gameSituation.getActiveState().activePlayer;

      return tempId;
    },
    preventMove(reason: string, options = {}) {
      this.movePreventReasons.push(Object.assign(options, { message: reason }));
    },
    allowInactivePlay() {
      this.inactiveCanPlay = true;
    },
    makeTurnHidden(tag: string = "__defaultTag") {
      this.turnHidden = tag;
    },
    makeTurnNonBlocking() {
      this.turnNonBlocking = true;
    },
    markActiveStateResolved(_gameSituation?: GameSituationBackend) {
      if (!gameSituationBackend?.gameState.current.subState) {
        throw new Error("Cannot mark resolved a root game state");
      }
      this.addGameMove(
        {
          type: "set gamestate variable",
          variableName: "__markResolved",
          newValue: true,
        },
        gameSituationBackend,
      );
    },
    addReplayMark(text: string) {
      this.replayMarks.push(text);
    },
    incStat: function (
      statName: string,
      statValue: StatValue,
      gameSituation?: GameSituationBackend,
      player: string | undefined = undefined,
      absolute: boolean = false,
      compare: boolean = false,
    ) {
      let newMove: IncStatData = {
        type: "inc stat",
        statName,
        statValue,
        player,
        absolute,
        compare,
      };
      evalGameMove(newMove, gameSituation);
      this.gameMoveList.push(JSON.parse(JSON.stringify(newMove)));
    },
    pushStat: function (
      statName: string,
      statValue: StatValue,
      gameSituation?: GameSituationBackend,
      player: string | undefined = undefined,
    ) {
      let newMove = {
        type: "push stat",
        statName,
        statValue,
        player,
      };
      evalGameMove(newMove, gameSituation);
      this.gameMoveList.push(JSON.parse(JSON.stringify(newMove)));
    },
    setObjectStat: function (
      objectName: string,
      fieldName: string,
      statValue: StatValue,
      absolute: boolean,
      gameSituation: GameSituationBackend = gameSituationBackend,
    ) {
      let newMove = {
        type: "set object stat",
        objectName,
        fieldName,
        statValue,
        absolute,
      };
      evalGameMove(newMove, gameSituation);
      this.gameMoveList.push(JSON.parse(JSON.stringify(newMove)));
    },
    playSound: function (path: string, options: { [key: string]: any }) {
      let sound = new Audio(getImage(path, false));
      sound.volume = options.volume;
      sound.play();
    },
    confirmTurn: function (message: string) {
      this.confirmMessages.push(message);
    },
    addGameMoves: function (moves: GameMoveForEval[], gameSituation?: GameSituationBackend) {
      for (let move of moves) {
        this.addGameMove(move, gameSituation);
      }
    },
    preventUndo: function (reason?: string) {
      this._preventUndo = reason ?? true;
      this.undoBreakpoint = true;
    },
    setUndoBreakpoint: function () {
      this.undoBreakpoint = true;
    },
    logMessage: "",
    setLogMessage: function (message: string) {
      this.logMessage = message;
    },
    createAlert: function (message: string, color: string, cheatable: boolean = false) {
      setAlert(message, color, cheatable);
    },
    newClick: function () {
      this._preventUndo = false;
      this.undoBreakpoint = false;
      this.inactiveCanPlay = false;
      this.gameMoveList = [];
      this.movePreventReasons = [];
      this.turnHidden = "";
      this.turnNonBlocking = false;
      this.confirmMessages = [];
      this.replayMarks = [];
      this.oldActivePlayer = tableStateRef.current?.getActiveState().activePlayer;
    },
    finalizeClick: function (
      componentData: GameComponentForGame,
      playerColor: string,
      newActivePlayer: string | undefined,
      origFunction: (
        componentData: ComponentData,
        gameSituation: GameSituationBackend,
        eventManager: EventManager,
        playerColor: string,
      ) => any,
    ) {
      if (this.showMovePrevent(false)) {
        this.sendMoves(this.gameMoveList.filter((e) => e.forceEvaluate));
        return false;
      }
      let activePlayer = this.oldActivePlayer;
      if (
        typeof activePlayer === "string" &&
        activePlayer !== sourcePlayer &&
        activePlayer !== "everyone" &&
        activePlayer !== "automa"
      ) {
        this.createAlert("You cannot play now: Wait for your turn", "red");
        return;
      }

      if (this.confirmMessages.length) {
        this.sendMoves(this.gameMoveList.filter((e) => e.forceEvaluate));
        createConfirm(this.confirmMessages);
      } else {
        this.sendMoves();
      }
    },
    sendMoves: function (moveList: GameMoveData[] | undefined = undefined) {
      if (!moveList) {
        moveList = this.gameMoveList;
      }
      let activePlayer = this.newActivePlayer;
      if (activePlayer === this.oldActivePlayer) {
        activePlayer = undefined;
      }
      if (turnToConfirm?.current !== -1) {
        this.createAlert(
          "Server hasn't confirmed your previous turn. You are playing too fast. Try again later. If issue persists, reload the page.",
          "red",
        );
        return;
      }
      let dataToSend = {
        moveList,
        replayMarks: this.replayMarks,
        activePlayer,
      };
      setCheatMode(false);
      if (moveList.length === 0) {
        return;
      }
      setStatusBarStatus("waitingResponse");
      let previousMoveId = allEventsRef.current
        .filter((e) => !e.data?.turnHidden && !e.data?.turnNonBlocking)
        .map((e) => e.data?.moveId)
        .at(-1);
      let moveId = previousMoveId !== undefined ? previousMoveId + 1 : 0;
      let turnUID = moveId + "-public";
      if (this.turnHidden) {
        turnUID = moveId + "-hidden-" + sourcePlayer + allEventsRef.current.length;
      }
      if (turnToConfirm) {
        turnToConfirm.current = moveId;
      }
      let finalData = Object.assign(
        {
          tableId: tableId,
          username: localStorage.getItem("user-name"),
          socketId: socket.id,
          moveId,
          turnUID,
          preventUndo: this._preventUndo,
          undoBreakpoint: this.undoBreakpoint,
          turnHidden: this.turnHidden,
          turnNonBlocking: this.turnNonBlocking,
          sourcePlayer,
        },
        dataToSend,
      );
      if (
        !this.gameMoveList.find((e) =>
          [
            "component reveal",
            "reveal to deck",
            "reveal all",
            "reshuffle",
            "randomize variable",
            "discard",
            "returnToTop",
            "return to top",
            "put to bottom",
            "reveal hidden turns",
            "table first visit",
          ].includes(e.type ?? ""),
        )
      ) {
        window.tableEventQueue.push({
          type: "game-move",
          sourcePlayer,
          timestamp: Date.now(),
          username: finalData.username ?? undefined,
          data: finalData,
        });
        if (window.tableEventQueue.length === 1) {
          evalNextTableEvent(true);
        }
      }
      socket?.emit("game move", JSON.stringify(finalData));
    },
    showMovePrevent: function (hideError: boolean) {
      if (cheatMode) {
        return false;
      }
      if (this.movePreventReasons.length) {
        let alertMessage = this.movePreventReasons[0].message;
        if (this.movePreventReasons.length > 1) {
          alertMessage = "Cannot make move: " + this.movePreventReasons.map((r) => r.message).join("; ");
        }
        if (!hideError) {
          setConfirmMovesToSend(this.gameMoveList);
          this.createAlert(alertMessage, "red", true);
        }
        return true;
      }
      return false;
    },
    newEvent: function (eventName: string, options: PlayerEvent) {
      newEventCallBack(eventName, options, this);
    },

    gameMoveList: [] as GameMoveData[],
  };
}
